// Document Storage Service - Handles document persistence using IndexedDB
// Files are stored as base64 strings for persistence across sessions

const DB_NAME = 'nexedu_documents';
const DB_VERSION = 1;
const STORE_NAME = 'documents';

export interface StoredDocument {
  id: string;
  userId: string;
  type: string;
  fileName: string;
  mimeType: string;
  size: number;
  base64Data: string;
  uploadedAt: string;
}

let db: IDBDatabase | null = null;

// Initialize IndexedDB
async function initDB(): Promise<IDBDatabase> {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('userId', 'userId', { unique: false });
        store.createIndex('userType', ['userId', 'type'], { unique: true });
      }
    };
  });
}

// Convert File to base64
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

// Convert base64 to Blob for preview
function base64ToBlob(base64: string): Blob {
  const parts = base64.split(',');
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  const bstr = atob(parts[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

// Get user ID from localStorage
function getUserId(): string | null {
  const saved = localStorage.getItem('nexedu_user');
  if (saved) {
    try {
      const user = JSON.parse(saved);
      return user.id;
    } catch {
      return null;
    }
  }
  return null;
}

// Save a document
export async function saveDocument(
  type: string,
  file: File
): Promise<StoredDocument> {
  const userId = getUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }

  const database = await initDB();
  const base64Data = await fileToBase64(file);

  const doc: StoredDocument = {
    id: `${userId}_${type}`,
    userId,
    type,
    fileName: file.name,
    mimeType: file.type,
    size: file.size,
    base64Data,
    uploadedAt: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(doc);

    request.onsuccess = () => {
      console.log('Document saved:', type);
      resolve(doc);
    };

    request.onerror = () => {
      console.error('Failed to save document:', request.error);
      reject(request.error);
    };
  });
}

// Get a document by type
export async function getDocument(type: string): Promise<StoredDocument | null> {
  const userId = getUserId();
  if (!userId) return null;

  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(`${userId}_${type}`);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      console.error('Failed to get document:', request.error);
      reject(request.error);
    };
  });
}

// Get all documents for current user
export async function getAllDocuments(): Promise<StoredDocument[]> {
  const userId = getUserId();
  if (!userId) return [];

  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');
    const request = index.getAll(userId);

    request.onsuccess = () => {
      console.log('Retrieved documents:', request.result?.length || 0);
      resolve(request.result || []);
    };

    request.onerror = () => {
      console.error('Failed to get documents:', request.error);
      reject(request.error);
    };
  });
}

// Delete a document
export async function deleteDocument(type: string): Promise<void> {
  const userId = getUserId();
  if (!userId) return;

  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(`${userId}_${type}`);

    request.onsuccess = () => {
      console.log('Document deleted:', type);
      resolve();
    };

    request.onerror = () => {
      console.error('Failed to delete document:', request.error);
      reject(request.error);
    };
  });
}

// Get preview URL for a stored document
export function getDocumentPreviewUrl(doc: StoredDocument): string {
  const blob = base64ToBlob(doc.base64Data);
  return URL.createObjectURL(blob);
}

// Clear all documents for current user
export async function clearAllDocuments(): Promise<void> {
  const docs = await getAllDocuments();
  for (const doc of docs) {
    await deleteDocument(doc.type);
  }
}

// Convert a stored document back to a File object for uploading
export function storedDocumentToFile(doc: StoredDocument): File {
  const blob = base64ToBlob(doc.base64Data);
  return new File([blob], doc.fileName, { type: doc.mimeType });
}
